import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Button } from '../../ui';
import PremiumName from '../../ui/PremiumName';
import { useUserData } from '../../../hooks/useUserData';
import { formatPostDate, formatFullDate } from '../../../utils/dateFormatter';
import {
  addOrderMessage,
  uploadDeliverableFile,
  addDeliverablesToOrder,
  removeDeliverableFromOrder,
  uploadOrderChatAttachment,
  markOrderChatAsRead,
  updateOrderStatus
} from '../../../services/orderService';
import { PortfolioImageModal } from '../../portfolio';
import { db } from '../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';

const MAX_CHAT_FILES = 5;
const MAX_CHAT_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * OrderDetailsModal - Modal con detalles completos de una orden
 * Incluye timeline, mensajes y acciones
 */
const OrderDetailsModal = ({ order, viewAs, onClose, onAction }) => {
  const [activeTab, setActiveTab] = useState('detalles'); // detalles | chat
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  const [deliverableFiles, setDeliverableFiles] = useState([]);
  const [uploadingDeliverables, setUploadingDeliverables] = useState(false);
  const [removingDeliverableIndex, setRemovingDeliverableIndex] = useState(null);
  const deliverableInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const [chatFiles, setChatFiles] = useState([]);
  const messagesContainerRef = useRef(null);

  const chatCount = Array.isArray(displayMessages) ? displayMessages.length : 0;

  // Mantener el chat en vivo: escuchar cambios en la orden (messages) en tiempo real.
  // Esto evita el bug donde los mensajes no aparecen hasta que el usuario envía otro.
  useEffect(() => {
    if (!order?.id) return;
    const orderRef = doc(db, 'ServiceOrders', order.id);
    const unsubscribe = onSnapshot(
      orderRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const serverMessages = Array.isArray(data.messages) ? data.messages : [];
        setDisplayMessages(serverMessages);
      },
      (err) => {
        console.error('Error al escuchar mensajes de la orden:', err);
      }
    );
    return () => unsubscribe();
  }, [order?.id]);

  // Al abrir la pestaña de chat, marcar como leído para el usuario actual
  useEffect(() => {
    if (activeTab !== 'chat') return;
    if (!order?.id) return;
    markOrderChatAsRead(order.id, viewAs === 'client' ? 'client' : 'seller').then((res) => {
      if (res?.success && onAction) onAction(order.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, order?.id, viewAs]);

  useEffect(() => {
    if (activeTab !== 'chat') return;
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };
    const t = setTimeout(scrollToBottom, 0);
    return () => clearTimeout(t);
  }, [displayMessages, activeTab]);

  const referenceFiles = order.referenceFiles && Array.isArray(order.referenceFiles) ? order.referenceFiles : [];
  const referenceImages = referenceFiles.map((url, i) => ({ url, id: `ref-${order.id}-${i}` }));

  const otherUserId = viewAs === 'client' ? order.sellerId : order.clientId;
  const otherUserName = viewAs === 'client' ? order.sellerName : order.clientName;
  const { userData: otherUserData } = useUserData(otherUserId);
  const { userData: sellerUserData } = useUserData(order.sellerId);

  // Prevenir scroll del body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSendMessage = async () => {
    const text = (newMessage || '').trim();
    const files = Array.isArray(chatFiles) ? chatFiles : [];
    if (!text && files.length === 0) return;
    if (isSendingMessage) return;

    const fromRole = viewAs === 'client' ? 'client' : 'seller';
    const optimisticMessage = {
      from: fromRole,
      message: text,
      timestamp: new Date(),
      read: false,
      _optimistic: true
    };

    setDisplayMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    setChatFiles([]);
    setIsSendingMessage(true);

    try {
      const uploadable = files.slice(0, MAX_CHAT_FILES);
      const attachments = [];
      for (const file of uploadable) {
        if (file?.size > MAX_CHAT_ATTACHMENT_BYTES) {
          alert(`"${file.name}" excede 10MB y no se enviará.`);
          continue;
        }
        const up = await uploadOrderChatAttachment(order.id, file);
        if (up.success && up.attachment?.url) {
          attachments.push(up.attachment);
        } else if (up.error) {
          alert(`No se pudo subir "${file.name}": ${up.error}`);
        }
      }

      if (!text && attachments.length === 0) {
        setDisplayMessages(prev => prev.filter(m => m._optimistic !== true));
        setIsSendingMessage(false);
        return;
      }

      const result = await addOrderMessage(order.id, text, attachments);
      if (result.success) {
        if (onAction) onAction(order.id);
        setDisplayMessages(prev => prev.map(m => (m._optimistic ? { ...m, _optimistic: false } : m)));
      } else {
        setDisplayMessages(prev => prev.filter(m => m._optimistic !== true));
        alert('Error al enviar mensaje: ' + result.error);
      }
    } catch (err) {
      setDisplayMessages(prev => prev.filter(m => m._optimistic !== true));
      alert('Error al enviar mensaje: ' + (err?.message || 'Error desconocido'));
    } finally {
      setIsSendingMessage(false);
    }
  };

  const hasPendingMessage = displayMessages.some(m => m._optimistic);

  // Timeline de estados
  const timeline = [
    { 
      status: 'pending', 
      label: 'Solicitado', 
      completed: true,
      date: order.createdAt 
    },
    { 
      status: 'accepted', 
      label: 'Aceptado', 
      completed: order.status !== 'pending' && order.status !== 'cancelled',
      date: order.acceptedAt 
    },
    {
      status: 'payment_requested',
      label: 'Pago solicitado',
      completed: order.status === 'payment_requested' || order.status === 'in_progress' || order.status === 'completed',
      date: order.paymentRequestedAt
    },
    { 
      status: 'in_progress', 
      label: 'En Progreso', 
      completed: order.status === 'in_progress' || order.status === 'completed',
      date: order.startedAt 
    },
    { 
      status: 'completed', 
      label: 'Completado', 
      completed: order.status === 'completed',
      date: order.completedAt 
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header compacto */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate pr-4">
            Detalles de la Orden
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex gap-6">
            <button
              type="button"
              onClick={() => setActiveTab('detalles')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'detalles'
                  ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Detalles
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('chat')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'chat'
                  ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Chat y perfil ({chatCount})
            </button>
          </div>
        </div>

        {/* Body: contenido según tab */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'detalles' && (
            <div className="space-y-5">
              {/* Resumen del servicio (barra compacta) */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                {order.serviceImage && (
                  <img
                    src={order.serviceImage}
                    alt={order.serviceName}
                    className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 dark:text-white truncate">
                    {order.serviceName}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Paquete: <span className="font-medium">{order.packageName}</span>
                    <span className="mx-2">·</span>
                    <span className="font-bold text-indigo-600 dark:text-indigo-400">
                      ${order.amount} {order.currency}
                    </span>
                  </p>
                </div>
              </div>

              {/* Timeline horizontal compacto */}
              {order.status !== 'cancelled' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Progreso
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {timeline.map((step) => (
                      <div
                        key={step.status}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
                          step.completed
                            ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-400'
                        }`}
                      >
                        {step.completed ? (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="w-3.5 h-3.5 rounded-full bg-current opacity-50" />
                        )}
                        {step.label}
                        {step.date && (
                          <span className="opacity-75">· {formatFullDate(step.date)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Descripción del pedido */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Descripción del pedido
                </h3>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {order.description}
                  </p>
                  {order.deadline && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Fecha límite: <span className="font-medium">{order.deadline}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Imágenes de referencia del contratante */}
              {referenceImages.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Imágenes de referencia del contratante
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {referenceImages.map((img, index) => (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => {
                          setImageViewerIndex(index);
                          setImageViewerOpen(true);
                        }}
                        className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-400 transition-all flex-shrink-0"
                      >
                        <img src={img.url} alt={`Referencia ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Métodos de pago del vendedor */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Métodos de pago del vendedor
                </h3>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  {(() => {
                    const pm = sellerUserData?.paymentMethods || {};
                    const hasCvu = pm.cvu?.active && (pm.cvu?.alias?.trim() || pm.cvu?.cvu?.trim());
                    const hasPaypal = pm.paypal?.active && pm.paypal?.email?.trim();
                    const hasCrypto = pm.crypto?.active && pm.crypto?.address?.trim();
                    if (!hasCvu && !hasPaypal && !hasCrypto) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          El vendedor aún no ha configurado métodos de pago.
                        </p>
                      );
                    }
                    return (
                      <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                        {hasCvu && (
                          <li>
                            <span className="font-medium text-gray-900 dark:text-white">Alias / CVU:</span>{' '}
                            {[pm.cvu.alias?.trim(), pm.cvu.cvu?.trim()].filter(Boolean).join(' · ')}
                          </li>
                        )}
                        {hasPaypal && (
                          <li>
                            <span className="font-medium text-gray-900 dark:text-white">PayPal:</span>{' '}
                            {pm.paypal.email.trim()}
                          </li>
                        )}
                        {hasCrypto && (
                          <li>
                            <span className="font-medium text-gray-900 dark:text-white">
                              Cripto{pm.crypto.label?.trim() ? ` (${pm.crypto.label.trim()})` : ''}:
                            </span>{' '}
                            {pm.crypto.address.trim()}
                          </li>
                        )}
                      </ul>
                    );
                  })()}
                </div>
              </div>

              {/* Solicitar pago (solo vendedor, orden aceptada) */}
              {viewAs === 'seller' && order.status === 'accepted' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-amber-900 dark:text-amber-200">
                        Solicitar pago al comprador
                      </p>
                      <p className="text-sm text-amber-800/80 dark:text-amber-200/80">
                        Se notificará al contratante para que realice el pago del servicio.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm('¿Solicitar pago al comprador?')) return;
                        const res = await updateOrderStatus(order.id, 'payment_requested');
                        if (res.success) {
                          if (onAction) onAction(order.id);
                          alert('Pago solicitado. Se notificó al comprador.');
                        } else {
                          alert(res.error || 'No se pudo solicitar el pago');
                        }
                      }}
                    >
                      Solicitar pago
                    </Button>
                  </div>
                </div>
              )}

              {/* Continuar después de solicitar pago */}
              {viewAs === 'seller' && order.status === 'payment_requested' && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-indigo-900 dark:text-indigo-200">
                        Pago solicitado
                      </p>
                      <p className="text-sm text-indigo-800/80 dark:text-indigo-200/80">
                        Cuando estés listo, podés continuar el servicio pasando el pedido a “En progreso”.
                      </p>
                    </div>
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm('¿Pasar el pedido a En progreso?')) return;
                        const res = await updateOrderStatus(order.id, 'in_progress');
                        if (res.success) {
                          if (onAction) onAction(order.id);
                          alert('Pedido pasado a En progreso.');
                        } else {
                          alert(res.error || 'No se pudo actualizar el estado');
                        }
                      }}
                    >
                      Pasar a en progreso
                    </Button>
                  </div>
                </div>
              )}

              {viewAs === 'client' && order.status === 'payment_requested' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    El vendedor solicitó el pago
                  </p>
                  <p className="text-sm text-amber-800/80 dark:text-amber-200/80">
                    Revisá los métodos de pago del vendedor y coordiná por el chat.
                  </p>
                  <div className="mt-3">
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('chat')}>
                      Ir al chat
                    </Button>
                  </div>
                </div>
              )}

              {/* Subir entregables (solo vendedor, orden en progreso o completada) */}
              {viewAs === 'seller' && (order.status === 'in_progress' || order.status === 'completed') && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Enviar archivos al comprador
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Imágenes o archivos en calidad original; el comprador podrá descargarlos.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <input
                      ref={deliverableInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.psd,.ai,.zip,.rar,.mp4,.mov,.webm"
                      className="hidden"
                      onChange={(e) => {
                        const files = e.target.files ? Array.from(e.target.files) : [];
                        setDeliverableFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deliverableInputRef.current?.click()}
                      disabled={uploadingDeliverables}
                    >
                      Seleccionar archivos
                    </Button>
                    {deliverableFiles.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {deliverableFiles.length} archivo(s)
                        </span>
                        <button
                          type="button"
                          onClick={() => setDeliverableFiles([])}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                        >
                          Limpiar
                        </button>
                      </>
                    )}
                  </div>
                  {deliverableFiles.length > 0 && (
                    <Button
                      variant="gradient"
                      size="sm"
                      disabled={uploadingDeliverables}
                      onClick={async () => {
                        setUploadingDeliverables(true);
                        const uploaded = [];
                        for (const file of deliverableFiles) {
                          const res = await uploadDeliverableFile(order.id, file);
                          if (res.success && res.url) uploaded.push({ url: res.url, name: res.name });
                          else if (res.error) alert(res.error);
                        }
                        if (uploaded.length > 0) {
                          const addRes = await addDeliverablesToOrder(order.id, uploaded);
                          if (addRes.success) {
                            setDeliverableFiles([]);
                            if (onAction) onAction(order.id);
                          } else alert(addRes.error || 'Error al guardar.');
                        }
                        setUploadingDeliverables(false);
                      }}
                    >
                      {uploadingDeliverables ? 'Subiendo...' : 'Subir y enviar'}
                    </Button>
                  )}
                </div>
              )}

              {/* Archivos entregados (visible cuando hay entregables) */}
              {order.deliverables && order.deliverables.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Archivos entregados
                  </h3>
                  <div className="space-y-1.5">
                    {order.deliverables.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-sm group"
                      >
                        <a
                          href={file.url}
                          download={file.name || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 flex-1 min-w-0"
                        >
                          <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {file.name || `Archivo ${index + 1}`}
                          </span>
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                        {viewAs === 'seller' && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              if (removingDeliverableIndex !== null) return;
                              if (!window.confirm('¿Eliminar este archivo de los entregables?')) return;
                              setRemovingDeliverableIndex(index);
                              const res = await removeDeliverableFromOrder(order.id, index);
                              setRemovingDeliverableIndex(null);
                              if (res.success && onAction) onAction(order.id);
                              else if (res.error) alert(res.error);
                            }}
                            disabled={removingDeliverableIndex !== null}
                            className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0 disabled:opacity-50"
                            aria-label="Eliminar archivo"
                            title="Eliminar archivo"
                          >
                            {removingDeliverableIndex === index ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="space-y-4 w-full flex flex-col min-h-0">
              {/* Tarjeta del otro usuario */}
              <div className="flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  {viewAs === 'client' ? 'Vendedor' : 'Comprador'}
                </h3>
                <Link
                  to={`/profile/${otherUserId}`}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:opacity-90 transition-opacity w-full"
                >
                  <Avatar
                    src={otherUserData?.photoURL}
                    alt={otherUserName}
                    fallbackText={otherUserName}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <PremiumName
                      name={otherUserData?.displayName || otherUserName || 'Usuario'}
                      isPremium={otherUserData?.isPremium}
                      premiumColor={otherUserData?.premiumNameColor}
                      nameClassName="font-semibold text-gray-900 dark:text-white truncate block"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ver perfil →</p>
                  </div>
                </Link>
              </div>

              {/* Mensajes */}
              <div className="flex flex-col flex-1 min-h-[240px]">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex-shrink-0">
                  Mensajes
                </h3>
                <div
                  ref={messagesContainerRef}
                  className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-3 min-h-[200px] max-h-[320px] overflow-y-auto flex-1"
                >
                  {displayMessages.length > 0 ? (
                    displayMessages.map((msg, index) => {
                      const isCurrentUser = (viewAs === 'client' && msg.from === 'client') || 
                                           (viewAs === 'seller' && msg.from === 'seller');
                      const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
                      return (
                        <div
                          key={index}
                          className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                            isCurrentUser
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white'
                          }`}>
                            {!!msg.message && <p className="break-words">{msg.message}</p>}
                            {attachments.length > 0 && (
                              <div className={`mt-2 space-y-1.5 ${isCurrentUser ? 'text-indigo-100' : 'text-gray-700 dark:text-gray-200'}`}>
                                {attachments.map((a, i) => {
                                  const isImg = typeof a?.type === 'string' && a.type.startsWith('image/');
                                  const name = a?.name || `Adjunto ${i + 1}`;
                                  const url = a?.url;
                                  if (isImg && url) {
                                    return (
                                      <a
                                        key={`${index}-att-${i}`}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                        title={name}
                                      >
                                        <img
                                          src={url}
                                          alt={name}
                                          className="max-w-[220px] max-h-[180px] rounded-md border border-black/10 dark:border-white/10"
                                        />
                                      </a>
                                    );
                                  }
                                  return (
                                    <a
                                      key={`${index}-att-${i}`}
                                      href={url || '#'}
                                      target={url ? '_blank' : undefined}
                                      rel={url ? 'noopener noreferrer' : undefined}
                                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border ${
                                        isCurrentUser
                                          ? 'border-indigo-300/30 bg-indigo-500/20'
                                          : 'border-gray-200 dark:border-slate-500 bg-white/60 dark:bg-slate-700/40'
                                      } ${url ? 'hover:opacity-90' : 'opacity-70 cursor-default'}`}
                                      onClick={(e) => {
                                        if (!url) e.preventDefault();
                                      }}
                                      title={name}
                                    >
                                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 5.656l1.414 1.414a4 4 0 005.656-5.656l-1.414-1.414z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7h.01" />
                                      </svg>
                                      <span className="truncate text-xs font-medium">{name}</span>
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                            <p className={`text-xs mt-0.5 ${isCurrentUser ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
                              {formatPostDate(msg.timestamp)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                      No hay mensajes aún
                    </p>
                  )}
                </div>
                {order.status !== 'completed' && order.status !== 'cancelled' && (
                  <div className="mt-3 w-full flex-shrink-0 space-y-2">
                    <input
                      ref={chatFileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = e.target.files ? Array.from(e.target.files) : [];
                        if (files.length === 0) return;
                        setChatFiles((prev) => {
                          const next = [...(prev || []), ...files].slice(0, MAX_CHAT_FILES);
                          return next;
                        });
                        e.target.value = '';
                      }}
                    />
                    {chatFiles.length > 0 && (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          Adjuntos: <span className="font-semibold">{chatFiles.length}</span> (máx. {MAX_CHAT_FILES})
                        </p>
                        <button
                          type="button"
                          onClick={() => setChatFiles([])}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                        >
                          Limpiar
                        </button>
                      </div>
                    )}
                    {chatFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {chatFiles.map((f, i) => (
                          <span
                            key={`${f.name}-${i}`}
                            className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200"
                            title={f.name}
                          >
                            <span className="max-w-[220px] truncate">{f.name}</span>
                            <button
                              type="button"
                              onClick={() => setChatFiles((prev) => (prev || []).filter((_, idx) => idx !== i))}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                              aria-label="Quitar adjunto"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  <div className="flex gap-2 w-full">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !isSendingMessage) handleSendMessage();
                      }}
                      placeholder="Escribe un mensaje..."
                      className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      disabled={isSendingMessage}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => chatFileInputRef.current?.click()}
                      disabled={isSendingMessage || chatFiles.length >= MAX_CHAT_FILES}
                      className="flex-shrink-0"
                      title="Adjuntar archivo"
                    >
                      Adjuntar
                    </Button>
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={handleSendMessage}
                      disabled={(!newMessage.trim() && chatFiles.length === 0) || hasPendingMessage || isSendingMessage}
                      className="flex-shrink-0"
                    >
                      {isSendingMessage ? 'Enviando…' : 'Enviar'}
                    </Button>
                  </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex-shrink-0">
          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>

      {/* Visor de imágenes de referencia */}
      {imageViewerOpen && referenceImages.length > 0 && (
        <PortfolioImageModal
          images={referenceImages}
          currentIndex={imageViewerIndex}
          onClose={() => setImageViewerOpen(false)}
          onNavigate={setImageViewerIndex}
        />
      )}
    </div>
  );
};

export default OrderDetailsModal;
